import React, { useState } from 'react';
import {
    Button,
    Box,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    CircularProgress,
    useTheme,
} from '@mui/material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Import Open Sans for your UI styling
import '@fontsource/open-sans';
import { useTranslation } from 'react-i18next';

import { organization } from '../../../config/config';
import BadNauheimLogo from '../../../assets/img/OrganizationLogos/BadNauheim-Logo.png';
import KemptenLogo from '../../../assets/img/OrganizationLogos/Kempten-Logo.png';
import LausitzLogo from '../../../assets/img/OrganizationLogos/MoveToLausitz-Logo.png';
import SIMALogo from '../../../assets/img/OrganizationLogos/SIMA-Logo.png';
import DietzenbachLogo from '../../../assets/img/OrganizationLogos/Dietzenbach-Logo.png';
import Rodgau from '../../../assets/img/OrganizationLogos/Rodgau-Logo.png';
import Ekom21Logo from '../../../assets/img/OrganizationLogos/Ekom21.png';
import BadHomburgLogo from '../../../assets/img/OrganizationLogos/BadHomburg-Logo.png';
import RastattLogo from '../../../assets/img/OrganizationLogos/Rastatt-Logo.png';
import RotenburgLogo from '../../../assets/img/OrganizationLogos/Rotenburg-Logo.png';
import Demo from '../../../assets/img/logo-ui-square-dark.png';
import customerLogoBase64 from '../../../assets/img/logo-ui-square-dark.png';
import { FaCloudDownloadAlt } from 'react-icons/fa';
import FrankenbergLogo from '../../../assets/img/OrganizationLogos/Frankenberg-Logo.png';
import ULILogo from '../../../assets/img/OrganizationLogos/ULI-Logo.png';
import FLIRLogo from '../../../assets/img/OrganizationLogos/FLIR-Logo.png';
import MobydexLogo from '../../../assets/img/OrganizationLogos/Mobidex-Logo.png';
import OffenburgLogo from '../../../assets/img/OrganizationLogos/Offenburg-Logo.png';

// Use a Record to allow indexing with a string key
const logoMap: Record<string, string> = {
    badnauheim: BadNauheimLogo,
    kempten: KemptenLogo,
    lausitz: LausitzLogo,
    sima: SIMALogo,
    dietzenbach: DietzenbachLogo,
    rodgau: Rodgau,
    movetolausitz: LausitzLogo,
    ekom21: Ekom21Logo,
    badhomburg: BadHomburgLogo,
    rastatt: RastattLogo,
    demo: Demo,
    rotenburg: RotenburgLogo,
    frankenberg: FrankenbergLogo,
    uli: ULILogo,
    flir: FLIRLogo,
    mobydex: MobydexLogo,
    offenburg: OffenburgLogo,
};

// Common styles for the text fields
const commonSx = {
    width: '100%',
    '& .MuiInputBase-root': {
        width: '100% !important',
        padding: 0,
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderBottom: '0 solid transparent',
    },
    '& .MuiInputBase-input': {
        padding: '8px 0',
    },
};

// Convert organization to lowercase for safe indexing
const ourLogo =
    organization && logoMap[organization.toLowerCase()]
        ? logoMap[organization.toLowerCase()]
        : Demo;

function generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

function generateShortUUID(): string {
    let id = '';
    for (let i = 0; i < 10; i++) {
        const random = Math.floor(Math.random() * 16);
        id += random.toString(16);
    }
    return id;
}

function getFormattedDate(): string {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();
    return `${yyyy}${mm}${dd}`;
}

// Helper function to load image and get dimensions
function loadImage(src: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            resolve({ width: img.width, height: img.height });
        };
        img.onerror = () => {
            // Return default dimensions if image fails to load
            resolve({ width: 100, height: 100 });
        };
        img.src = src;
    });
}

const DownloadPageButton: React.FC = () => {
    const theme = useTheme();
    const { t } = useTranslation(); // Use destructured t function
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [erlaeuterung, setErlaeuterung] = useState('');
    const [datenquellen, setDatenquellen] = useState('');
    const [downloading, setDownloading] = useState(false);

    const handleOpen = () => setOpen(true);

    const handleClose = () => {
        setTitle('');
        setErlaeuterung('');
        setDatenquellen('');
        setOpen(false);
    };

    const handleDownload = async () => {
        setDownloading(true);

        try {
            const elements = document.querySelectorAll(
                '#downloadable-content .downloadable-plot-wrapper'
            );
            if (!elements.length) {
                alert(t('common.pdf.alert_no_plots'));
                setDownloading(false);
                return;
            }

            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'pt',
                format: 'a4',
                compress: true,
            });

            // --- HEADER SECTION ---
            const margin = 20;
            let currentY = margin;
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // Load and get logo dimensions
            const logoDimensions = await loadImage(ourLogo);
            const logoAspectRatio = logoDimensions.width / logoDimensions.height;

            // Fixed logo height and calculated width based on actual logo dimensions
            const logoHeight = 30;
            const logoWidth = logoHeight * logoAspectRatio;

            // Validate dimensions before adding image
            if (logoWidth > 0 && logoHeight > 0 && !isNaN(logoWidth) && !isNaN(logoHeight)) {
                pdf.addImage(ourLogo, 'jpeg', margin, margin, logoWidth, logoHeight);
            }

            // Load and get customer logo dimensions
            const customerLogoDimensions = await loadImage(customerLogoBase64);
            const customerLogoAspectRatio = customerLogoDimensions.width / customerLogoDimensions.height;

            const now = new Date();
            const dateString = now.toLocaleDateString('de-DE');
            const timeString = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
            const uuid = generateShortUUID();
            pdf.setFontSize(12);
            const subHeader = `${dateString}, ${timeString} ${t('common.pdf.timeSuffix')} - ${t('common.pdf.reportReference')}: ${uuid}`;
            const subHeaderWidth = pdf.getTextWidth(subHeader);
            pdf.text(subHeader, (pageWidth - subHeaderWidth) / 2, margin + 40);

            currentY = margin + 60;

            // Fixed customer logo height and calculated width based on actual logo dimensions
            const customerLogoHeight = 50;
            const customerLogoWidth = customerLogoHeight * customerLogoAspectRatio;

            // Validate dimensions before adding customer logo
            if (customerLogoWidth > 0 && customerLogoHeight > 0 && !isNaN(customerLogoWidth) && !isNaN(customerLogoHeight)) {
                pdf.addImage(
                    customerLogoBase64,
                    'jpeg',
                    pageWidth - margin - customerLogoWidth,
                    margin,
                    customerLogoWidth,
                    customerLogoHeight
                );
            }

            currentY = margin + logoHeight + 20;

            // Set font for the header text
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(18);
            let headerText = title ? title : t('common.pdf.reportTitle');

            // Adjusted text area to account for larger logos
            const textAreaWidth = pageWidth - 2 * margin - logoWidth - customerLogoWidth - 30;
            const headerLines = pdf.splitTextToSize(headerText, textAreaWidth);

            // Center the text vertically with the logos
            const textStartY = margin + (logoHeight / 2) - ((headerLines.length * 20) / 2) + 10;
            pdf.text(headerLines, (pageWidth - pdf.getTextWidth(headerLines[0])) / 2, textStartY);

            // --- EXPLANATION SECTION ---
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.text(t('common.pdf.explanation') + ":", margin, currentY);
            currentY += 14;

            pdf.setFont('open-sans', 'normal');
            const explanationLines = pdf.splitTextToSize(
                erlaeuterung,
                pageWidth - 2 * margin
            );
            pdf.text(explanationLines, margin, currentY);
            currentY += explanationLines.length * 14 + 10;
            currentY += 20;

            // Add extra white space before data sources
            pdf.setFont('helvetica', 'bold');
            pdf.text(t('common.pdf.dataSources') + ":", margin, currentY);
            currentY += 14;

            pdf.setFont('helvetica', 'normal');
            const dataSourcesLines = pdf.splitTextToSize(
                datenquellen,
                pageWidth - 2 * margin
            );
            pdf.text(dataSourcesLines, margin, currentY);
            currentY += dataSourcesLines.length * 14 + 10;

            const firstPageAvailableHeight = pageHeight - currentY - margin;

            // --- CONTENT SECTION ---
            for (const el of Array.from(elements)) {
                // Cast queried elements to HTMLElement to avoid TS errors
                const captionEl = el.querySelector('.plot-caption-text') as HTMLElement | null;
                const captionText = captionEl ? captionEl.innerText.trim() : '';

                const filtersEl = el.querySelector('.plot-filters-text') as HTMLElement | null;
                const filtersText = filtersEl ? filtersEl.innerText.trim() : '';

                const canvas = await html2canvas(el as HTMLElement, {
                    scale: 2,
                    useCORS: true,
                });
                const imgData = canvas.toDataURL('image/png');
                const maxImgWidth = pageWidth - margin * 2;

                const isFirstPage = pdf.getNumberOfPages() === 1;
                const availableHeight = isFirstPage ? firstPageAvailableHeight : pageHeight - margin;

                const widthScale = maxImgWidth / canvas.width;
                const heightScale = availableHeight / canvas.height;
                const scaleFactor = Math.min(1, widthScale, heightScale);

                const scaledImgWidth = canvas.width * scaleFactor;
                const scaledImgHeight = canvas.height * scaleFactor;

                // Validate scaled dimensions
                if (scaledImgWidth <= 0 || scaledImgHeight <= 0 || isNaN(scaledImgWidth) || isNaN(scaledImgHeight)) {
                    console.warn(t('common.pdf.invalidImageDimensions'));
                    continue;
                }

                const captionHeight = captionText ? 20 : 0;
                const filtersHeight = filtersText ? 34 : 0;
                const totalBlockHeight = captionHeight + filtersHeight + scaledImgHeight;

                if (currentY + totalBlockHeight > pageHeight - margin) {
                    pdf.addPage();
                    currentY = margin;
                }

                if (captionText) {
                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(12);
                    pdf.text(captionText, margin, currentY);
                    currentY += 20;
                    pdf.setFont('helvetica', 'normal');
                }

                if (filtersText) {
                    pdf.setFontSize(10);

                    pdf.setFont("helvetica", "bold");
                    pdf.text(t('common.pdf.filter'), margin, currentY);

                    pdf.setFont("helvetica", "normal");
                    currentY += 14;

                    pdf.text(filtersText, margin, currentY);
                    currentY += 20;
                }

                const xPosition = margin + (maxImgWidth - scaledImgWidth) / 2;
                pdf.addImage(imgData, 'PNG', xPosition, currentY, scaledImgWidth, scaledImgHeight);
                currentY += scaledImgHeight + 20;
            }

            const totalPages = pdf.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i);
                const pageWidth = pdf.internal.pageSize.getWidth();
                const pageHeight = pdf.internal.pageSize.getHeight();
                const pageNumberText = `${i}/${totalPages}`;
                const textWidth = pdf.getTextWidth(pageNumberText);
                pdf.text(pageNumberText, pageWidth - margin - textWidth, pageHeight - 10);
            }

            const fileName = `${getFormattedDate()}_${organization}_datalab_report`;
            pdf.save(fileName);

        } catch (error) {
            console.error(t('common.pdf.errorGenerating'), error);
            alert(t('common.pdf.errorMessage'));
        } finally {
            setDownloading(false);
            handleClose();
        }
    };

    return (
        <>
            <Button
                onClick={handleOpen}
                variant="outlined"
                startIcon={<FaCloudDownloadAlt />}
                sx={{
                    border: '1px solid',
                    backgroundColor: 'transparent',
                    marginTop: '16px',
                }}
            >
                {t('common.buttons.download')}
            </Button>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                PaperProps={{
                    sx: { border: 'none' },
                }}
            >
                <DialogContent
                    sx={{
                        backgroundColor: theme.palette.background.default,
                        border: 'none',
                    }}
                >
                    <Box sx={{ p: 2 }}>
                        <h2>{t('common.dialog.exportReport')}</h2>
                        <TextField
                            label={t('common.dialog.reportTitle')}
                            fullWidth
                            margin="normal"
                            value={title}
                            multiline
                            onChange={(e) => setTitle(e.target.value)}
                            sx={{
                                ...commonSx,
                                '& .MuiInputBase-input': {
                                    padding: '10px 14px',
                                },
                            }}
                        />
                        <TextField
                            label={t('common.dialog.explanation')}
                            fullWidth
                            margin="normal"
                            value={erlaeuterung}
                            rows={4}
                            multiline
                            onChange={(e) => setErlaeuterung(e.target.value)}
                            sx={{
                                ...commonSx,
                                '& .MuiInputBase-input': {
                                    padding: '10px 14px',
                                },
                            }}
                        />
                        <TextField
                            label={t('common.dialog.dataSources')}
                            fullWidth
                            rows={4}
                            multiline
                            margin="normal"
                            value={datenquellen}
                            onChange={(e) => setDatenquellen(e.target.value)}
                            sx={{
                                ...commonSx,
                                '& .MuiInputBase-input': {
                                    padding: '10px 14px',
                                },
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ backgroundColor: theme.palette.background.paper }}>
                    <Button
                        onClick={handleDownload}
                        variant="outlined"
                        startIcon={<FaCloudDownloadAlt />}
                        disabled={downloading}
                        sx={{
                            border: '1px solid',
                            backgroundColor: 'transparent',
                        }}
                    >
                        {downloading ? <CircularProgress size={20} color="inherit" /> : t('common.buttons.confirmDownload')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DownloadPageButton;